import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const services = [
  {
    id: 'manufacturing',
    title: 'Couch Manufacturing',
    subtitle: 'Custom-Built Excellence',
    description: 'Our expert craftsmen create bespoke couches tailored to your exact specifications. From the frame to the fabric, every detail is carefully considered to deliver a piece that perfectly fits your space and style.',
    icon: 'fa-industry',
    image: 'https://images.unsplash.com/photo-1564842505181-8862a3b9b173?w=800&q=80',
    features: [
      'Custom dimensions to fit any space',
      'Wide selection of fabrics and leathers',
      'Choice of foam densities for comfort',
      'Multiple frame options (wood, metal)',
      'Color matching and custom finishes',
      'Quality guarantee on all work'
    ],
    process: [
      { step: 1, title: 'Consultation', desc: 'Discuss your requirements and preferences' },
      { step: 2, title: 'Design', desc: 'Create detailed designs and material selection' },
      { step: 3, title: 'Crafting', desc: 'Expert construction in our workshop' },
      { step: 4, title: 'Delivery', desc: 'Professional delivery and setup' }
    ]
  },
  {
    id: 'sales',
    title: 'Couch Sales',
    subtitle: 'Ready-Made Luxury',
    description: 'Browse our curated collection of ready-made couches, designed for various styles and budgets. From modern minimalist to classic elegance, find the perfect piece for your home or office.',
    icon: 'fa-shopping-bag',
    image: 'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?w=800&q=80',
    features: [
      'Wide variety of styles and sizes',
      'Competitive pricing',
      'Immediate availability',
      'Showroom viewing available',
      'Flexible payment options',
      'Free delivery in Gauteng'
    ],
    process: [
      { step: 1, title: 'Browse', desc: 'Explore our collection online or in-store' },
      { step: 2, title: 'Select', desc: 'Choose your perfect piece' },
      { step: 3, title: 'Purchase', desc: 'Flexible payment options available' },
      { step: 4, title: 'Enjoy', desc: 'Fast delivery to your door' }
    ]
  },
  {
    id: 'reupholstery',
    title: 'Couch Reupholstery',
    subtitle: 'Transform & Restore',
    description: 'Give your beloved furniture a new lease on life. Our reupholstery service transforms worn, dated, or damaged couches into stunning pieces that look and feel brand new.',
    icon: 'fa-magic',
    image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&q=80',
    features: [
      'Complete fabric/leather replacement',
      'Foam and padding renewal',
      'Frame repair and reinforcement',
      'Spring system restoration',
      'Color and style transformation',
      'Eco-friendly option for sustainability'
    ],
    process: [
      { step: 1, title: 'Assessment', desc: 'Evaluate condition and discuss options' },
      { step: 2, title: 'Material Selection', desc: 'Choose new fabric/leather and foam' },
      { step: 3, title: 'Restoration', desc: 'Expert reupholstery in our workshop' },
      { step: 4, title: 'Return', desc: 'Delivery of your transformed couch' }
    ]
  }
];

export const ServicesPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <main data-testid="services-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=1920&q=80"
            alt="Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-leather/80"></div>
        </div>
        <div className="relative text-center z-10 px-4">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-gold font-body text-sm uppercase tracking-widest"
          >
            What We Offer
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mt-4"
          >
            Our Services
          </motion.h1>
        </div>
      </section>

      {/* Services Detail */}
      {services.map((service, index) => (
        <section 
          key={service.id}
          id={service.id}
          className={`section-padding ${index % 2 === 0 ? 'bg-cream' : 'bg-white'}`}
        >
          <div className="container-custom">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
              index % 2 === 1 ? 'lg:flex-row-reverse' : ''
            }`}>
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={index % 2 === 1 ? 'lg:order-2' : ''}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-gold/10 flex items-center justify-center">
                    <i className={`fas ${service.icon} text-gold text-2xl`}></i>
                  </div>
                  <div>
                    <p className="text-gold font-body text-sm uppercase tracking-wide">
                      {service.subtitle}
                    </p>
                    <h2 className="font-heading text-3xl md:text-4xl text-leather">
                      {service.title}
                    </h2>
                  </div>
                </div>

                <p className="text-leather/70 font-body leading-relaxed mb-8">
                  {service.description}
                </p>

                {/* Features */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                  {service.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <i className="fas fa-check text-gold mt-1"></i>
                      <span className="text-leather/70 font-body text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-4">
                  <Link to="/quote">
                    <Button 
                      data-testid={`service-quote-btn-${service.id}`}
                      className="btn-gold rounded-full px-8"
                    >
                      Get Quote
                      <i className="fas fa-arrow-right ml-2"></i>
                    </Button>
                  </Link>
                  <a href="https://wa.me/27752781104" target="_blank" rel="noopener noreferrer">
                    <Button 
                      variant="outline"
                      className="rounded-full px-8 border-leather text-leather hover:bg-leather hover:text-white"
                    >
                      <i className="fab fa-whatsapp mr-2"></i>
                      WhatsApp
                    </Button>
                  </a>
                </div>
              </motion.div>

              {/* Image & Process */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={index % 2 === 1 ? 'lg:order-1' : ''}
              >
                <div className="relative">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-80 object-cover rounded-2xl shadow-card"
                  />
                  
                  {/* Process Steps */}
                  <Card className="mt-8 border-0 shadow-card">
                    <CardContent className="p-6">
                      <h4 className="font-heading text-lg text-leather mb-4">Our Process</h4>
                      <div className="space-y-4">
                        {service.process.map((item) => (
                          <div key={item.step} className="flex items-start gap-4">
                            <div className="w-8 h-8 rounded-full bg-gold text-white flex items-center justify-center flex-shrink-0 font-body font-bold text-sm">
                              {item.step}
                            </div>
                            <div>
                              <p className="font-heading font-semibold text-leather">{item.title}</p>
                              <p className="text-leather/60 font-body text-sm">{item.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}
    </main>
  );
};

export default ServicesPage;
